import { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
// import pointIcon from "../../../../../../../../assets/icons/MapIcons/pointIcon.png";
import pointIconOrange from "assets/icons/MapIcons/locationMarkerOrange.png";
import rectangleIcon from "../../../../../../../../assets/icons/MapIcons/rectangle-tooltip-icon.png";
import pointIcon from "assets/icons/MapIcons/locationMarker.png";
import registerImageOnMap from "utils/registerImageOnMap";
import { sentenceCase } from "change-case";
import "./Popups.scss";

const usePaintClustersPoints = (
  clusterData: any,
  map: any,
  isMapFullyLoaded: boolean
) => {
  // Source & layer IDs
  const SRC = "earthquakes";
  const CLUSTER_LAYER = "clusters";
  const CLUSTER_COUNT = "cluster-count";
  const UNCLUSTERED = "unclustered-point";
  const HIGHLIGHT_ID = "clicked-point-highlight";

  // Refs to hold state across callbacks
  const highlightedCoordsRef = useRef<[number, number] | null>(null);
  const highlightedIdRef = useRef<string | number | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);

  useEffect(() => {
    if (!clusterData || !map || !isMapFullyLoaded) return;

    // Helper: remove highlight layer/source + popup
    function clearHighlight() {
      if (map.getLayer(HIGHLIGHT_ID)) {
        map.removeLayer(HIGHLIGHT_ID);
      }
      if (map.getSource(HIGHLIGHT_ID)) {
        map.removeSource(HIGHLIGHT_ID);
      }
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      highlightedCoordsRef.current = null;
      highlightedIdRef.current = null;
    }

    async function setupLayers() {
      // 1) Register marker images
      if (!map.hasImage("custom-marker")) {
        await registerImageOnMap(map, pointIcon, "custom-marker");
      }
      if (!map.hasImage("marker-orange-new")) {
        // parent already loaded this, but just in case:
        await registerImageOnMap(map, pointIconOrange, "marker-orange-new");
      }
      if (!map.hasImage("cluster-count-bg")) {
        await registerImageOnMap(map, rectangleIcon, "cluster-count-bg");
      }

      if (!map.getSource(SRC)) {
        // 2) Add clustered source
        map.addSource(SRC, {
          type: "geojson",
          data: clusterData,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50,
        });
      }

      // // 3) Cluster circles (symbol)
      // if (!map.getLayer(CLUSTER_LAYER)) {
      //   map.addLayer({
      //     id: CLUSTER_LAYER,
      //     type: "symbol",
      //     source: SRC,
      //     filter: ["has", "point_count"],
      //     layout: {
      //       "icon-image": "custom-marker",
      //       "icon-size": 1.5,
      //       "icon-allow-overlap": true,
      //     },
      //   });
      // }
      // if (!map.getLayer(CLUSTER_COUNT)) {

      // // 4) Cluster count labels
      //   map.addLayer({
      //     id: CLUSTER_COUNT,
      //     type: "symbol",
      //     source: SRC,
      //     filter: ["has", "point_count"],
      //     layout: {
      //       "icon-image": "cluster-count-bg",
      //       "icon-size": 0.3,
      //       "icon-allow-overlap": true,
      //       "text-field": "{point_count_abbreviated}",
      //       "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
      //       "text-size": 12,
      //       "text-anchor": "center",
      //       "text-offset": [0, -3],
      //       "icon-anchor": "bottom",
      //       "icon-offset": [0, -4],
      //     },
      //     paint: {
      //       "text-color": "#000",
      //     },
      //   });
      // }
      map.addLayer({
        id: "clusters",
        type: "circle",
        source: SRC,
        filter: ["has", "point_count"],
        paint: {
          "circle-color": [
            "step",
            ["get", "point_count"],
            "#11A3E2",
            100,
            "#42BBAC",
            999,
            "#BF200B",
          ],
          "circle-radius": [
            "step",
            ["get", "point_count"],
            15,
            100,
            20,
            999,
            30,
          ],
        },
      });

      map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: SRC,
        filter: ["has", "point_count"],
        layout: {
          "text-field": [
            "case",
            // Check if point count is 10,000 or more
            [">=", ["get", "point_count"], 10000],
            ["concat", ["to-string", ["/", ["get", "point_count"], 1000]], "k"], // Use "k" if 10,000 or more
            // Check if point count is 1000 or more but less than 10,000
            [">=", ["get", "point_count"], 1000],
            ["to-string", ["get", "point_count"]], // Display the full number for 1000 to 9999
            // Otherwise, display the full number for less than 1000
            ["to-string", ["get", "point_count"]],
          ],
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 12,
        },
        paint: {
          "text-color": "#FFFFFF",
        },
      });

      // 5) Unclustered-point layer
      if (!map.getLayer(UNCLUSTERED)) {
        map.addLayer({
          id: UNCLUSTERED,
          type: "symbol",
          source: SRC,
          filter: ["!", ["has", "point_count"]],
          layout: {
            "icon-image": "custom-marker",
            "icon-size": 1.5,
            "icon-allow-overlap": true,
          },
        });
      }
    }

    // 6) Cluster click to zoom in
    const onClusterClick = (
      e: mapboxgl.MapMouseEvent & {
        features?: mapboxgl.MapboxGeoJSONFeature[];
      }
    ) => {
      const feats: any = map.queryRenderedFeatures(e.point, {
        layers: [CLUSTER_LAYER],
      });
      const clusterId = feats[0]?.properties?.cluster_id as number;
      if (clusterId == null) return;

      (map.getSource(SRC) as mapboxgl.GeoJSONSource).getClusterExpansionZoom(
        clusterId,
        (err, zoom) => {
          if (err) return;
          const coords = feats[0].geometry.coordinates as [number, number];
          map.easeTo({ center: coords, zoom });
        }
      );
    };

    // 7) Unclustered click: highlight + popup
    const onUnclusteredClick = (e: mapboxgl.MapMouseEvent) => {
      clearHighlight();
      const feats: any = map.queryRenderedFeatures(e.point, {
        layers: [UNCLUSTERED],
      });
      if (!feats.length) return;

      const feat = feats[0];
      // use your feature’s unique ID
      const id = feat.properties?.id ?? feat.id;
      const coords = (
        feat.geometry.coordinates as [number, number]
      ).slice() as [number, number];
      const props = { ...feat.properties };

      highlightedCoordsRef.current = coords;
      highlightedIdRef.current = id;

      // add highlight source + layer, **using symbol** instead of circle
      map.addSource(HIGHLIGHT_ID, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: { type: "Point", coordinates: coords },
              properties: props,
            },
          ],
        },
      });
      map.addLayer({
        id: HIGHLIGHT_ID,
        type: "symbol",
        source: HIGHLIGHT_ID,
        layout: {
          "icon-image": "marker-orange-new",
          "icon-size": 1.5,
          "icon-allow-overlap": true,
        },
      });

      // show popup
      // const html = `<div class='flex flex-col items-center justify-center'><p class='obsCode-heading text-textNormal'>${props.observationCode}</p>
      //    </div>`;

      const html = `<div class='flex flex-col items-center justify-center'><p class='obsCode-heading text-textNormal'>${props.observationCode}</p>
              
    </div>`;

      popupRef.current = new mapboxgl.Popup({
        closeOnClick: true,
        className: "main-popups",
        closeButton: false,
      })
        .setLngLat(coords)
        .setHTML(html)
        .addTo(map);
    };

    // 8) On moveend: if that ID no longer exists in unclustered layer, clear
    const onMoveEnd = () => {
      const coords = highlightedCoordsRef.current;
      const id = highlightedIdRef.current;
      if (!coords || id == null) return;

      const pixel = map.project(coords);
      const hits = map.queryRenderedFeatures({
        layers: [UNCLUSTERED],
      });
      console.log("Hits:", hits);
      const stillHere = hits.some(
        (h: any) => (h.properties?.id ?? h.id) === id
      );
      if (!stillHere) {
        clearHighlight();
      }
    };

    // wire everything up
    setupLayers().then(() => {
      map.on("click", CLUSTER_LAYER, onClusterClick);
      map.on("mouseenter", CLUSTER_LAYER, () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", CLUSTER_LAYER, () => {
        map.getCanvas().style.cursor = "";
      });
      map.on("click", onUnclusteredClick);
      // map.on("moveend", onMoveEnd);
      map.on("move", onMoveEnd);
    });

    // cleanup on unmount or dependencies change
    return () => {
      clearHighlight();
      map.off("click", CLUSTER_LAYER, onClusterClick);
      map.off("mouseenter", CLUSTER_LAYER, () => {});
      map.off("mouseleave", CLUSTER_LAYER, () => {});
      map.off("click", onUnclusteredClick);
      map.off("moveend", onMoveEnd);
    };
  }, [clusterData, map, isMapFullyLoaded]);

  return null;
};

export { usePaintClustersPoints };
